import { Drawer, Table, Button, Tag, Space, Popconfirm, App } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useSurveyQuestions } from '../hooks/useSurveyQuestions'
import { deleteQuestion, updateQuestion } from '../services/survey-question-api'
import { QuestionFormModal } from './QuestionFormModal'
import type { SurveyQuestion } from '../types/survey.types'
import type { ColumnsType } from 'antd/es/table'

interface SurveyQuestionsDrawerProps {
  open: boolean
  surveyId: string | null
  surveyTitle: string | null
  onClose: () => void
}

export function SurveyQuestionsDrawer({ open, surveyId, surveyTitle, onClose }: SurveyQuestionsDrawerProps) {
  const { message } = App.useApp()
  const { questions, loading, refresh } = useSurveyQuestions(open ? surveyId : null)
  const [formOpen, setFormOpen] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState<SurveyQuestion | null>(null)

  const handleAdd = () => {
    setSelectedQuestion(null)
    setFormOpen(true)
  }

  const handleEdit = (question: SurveyQuestion) => {
    setSelectedQuestion(question)
    setFormOpen(true)
  }

  const handleDelete = async (question: SurveyQuestion) => {
    if (!surveyId) return
    try {
      await deleteQuestion(surveyId, question.id)

      const remaining = questions.filter(q => q.id !== question.id).sort((a, b) => a.step - b.step)

      await Promise.all(
        remaining.map((q, index) => {
          const newStep = index + 1
          if (q.step === newStep) return Promise.resolve()
          return updateQuestion(surveyId, q.id, { ...q, step: newStep })
        })
      )

      message.success('Question deleted')
      refresh()
    } catch (error: any) {
      console.error('Error deleting question:', error)
      message.error(error.response?.data?.message || 'Failed to delete question')
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setSelectedQuestion(null)
  }

  const handleFormSuccess = () => {
    refresh()
  }

  const parseOptions = (options: string | null): string[] => {
    if (!options) return []
    try {
      return JSON.parse(options)
    } catch {
      return []
    }
  }

  const typeColors: Record<string, string> = {
    'multi-select': 'blue',
    'single-select': 'green',
    text: 'orange'
  }

  const columns: ColumnsType<SurveyQuestion> = [
    {
      title: 'Step',
      dataIndex: 'step',
      key: 'step',
      width: 60,
      sorter: (a, b) => a.step - b.step,
      defaultSortOrder: 'ascend'
    },
    {
      title: 'Question',
      dataIndex: 'label',
      key: 'label',
      ellipsis: true
    },
    {
      title: 'Type',
      key: 'type',
      width: 150,
      render: (_, record) => (
        <Space size={4}>
          <Tag color={typeColors[record.type] || 'default'} style={{ fontSize: '11px', padding: '0 7px' }}>
            {record.type}
          </Tag>
          {record.isOptional && <Tag style={{ fontSize: '11px', padding: '0 7px' }}>optional</Tag>}
        </Space>
      )
    },
    {
      title: 'Options',
      key: 'options',
      width: 300,
      render: (_, record) => {
        const opts = parseOptions(record.options)
        if (opts.length === 0) return <span style={{ color: '#999' }}>-</span>
        return (
          <Space size={[4, 4]} wrap>
            {opts.map(opt => (
              <Tag key={opt} style={{ fontSize: '11px', padding: '0 7px' }}>
                {opt}
              </Tag>
            ))}
            {record.hasOther && (
              <Tag color="orange" style={{ fontSize: '11px', padding: '0 7px' }}>
                Others
              </Tag>
            )}
          </Space>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete question"
            description="Are you sure you want to delete this question?"
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <>
      <Drawer
        title={`Questions \u2014 ${surveyTitle || ''}`}
        open={open}
        onClose={onClose}
        width={1200}
        extra={
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleAdd}>
            Add question
          </Button>
        }
      >
        <Table
          size="small"
          columns={columns}
          dataSource={questions}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Drawer>

      {surveyId && (
        <QuestionFormModal
          open={formOpen}
          surveyId={surveyId}
          question={selectedQuestion}
          nextStep={questions.length > 0 ? Math.max(...questions.map(q => q.step)) + 1 : 1}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </>
  )
}
